import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Surface, Txt, useBreakpoints } from '@vst/beam';

import {
  selectHasFiberPlan,
  selectIsCafII,
  selectIsChoicePlan,
  selectIsDiamondPlan,
  selectIsUpsertPlanLoading,
  selectSelectedPlan,
} from '@buy-viasat/redux/src/plan';
import { selectEnv } from '@buy-viasat/redux/src/env';
import {
  CustomerType,
  selectCustomerType,
  selectIsModalVisible,
  selectIsVs2LargePlan,
  selectUrlParams,
} from '@buy-viasat/redux/src/app';
import { selectCartItems } from '@buy-viasat/redux/src/cart';

import i18n from '@buy-viasat/utils/src/lib/config/i18n';
import { AemFeature, createAemUrl } from '@buy-viasat/utils';
import { selectUpsertWithClosingOffersError } from '@buy-viasat/redux/src/closingOffers';

import ContentProvider from '../ContentProvider';
import { FiberPlanCartHeader } from './SelectedPlansName';
import { CheckoutCartServiceDetails } from './CheckoutCartServiceDetails';
import { CheckoutCartDisplayServiceDetails } from './CheckoutCartDisplayServiceDetails';
import { CheckoutCartTotalMonthly } from './CheckoutCartTotalMonthly';
import { CheckoutCartDueToday } from './CheckoutCartDueToday';
import { CheckoutCartTopper } from './CheckoutCartTopper';
import { CheckoutCartNewPlansDescription } from './CheckoutCartNewPlansDescription';
import { AddonItem, CheckoutCartProps, productFamilyAemPrefix, selectedAddons } from './types';
import CheckoutCartOptionalServices from './CheckoutCartOptionalServices';

import styles from './checkoutCart.module.scss';

export const CheckoutCart = ({ displayMobileCart, translationKeys }: CheckoutCartProps) => {
  const selectedPlan = useSelector(selectSelectedPlan);
  const { aemUrl } = useSelector(selectEnv);
  const isCafII = useSelector(selectIsCafII);
  const isVs2LargePlan = useSelector(selectIsVs2LargePlan);
  const isChoicePlan = useSelector(selectIsChoicePlan);
  const isDiamondPlan = useSelector(selectIsDiamondPlan);
  const cartItems = useSelector(selectCartItems);
  const isModalVisible = useSelector(selectIsModalVisible);

  const customerType = useSelector(selectCustomerType);
  const isBusinessUser = customerType === CustomerType.B;

  const { characteristics, name } = selectedPlan ?? { characteristics: null, name: '' };

  const hasFiberPlanInCart = useSelector(selectHasFiberPlan);
  const isUpsertPlanLoading = useSelector(selectIsUpsertPlanLoading);
  const isClosingOffersError: boolean = useSelector(selectUpsertWithClosingOffersError);

  const isBackgroundAPICallLoading: boolean = isUpsertPlanLoading || isClosingOffersError || isModalVisible;

  const [displayServiceDetails, setDisplayServiceDetails] = useState(true);
  const onDisplayServiceDetailsClick = () => setDisplayServiceDetails(!displayServiceDetails);
  const isMobileCartOpened = displayMobileCart ? styles['cartOpened'] : styles['cartClosed'];

  const { viewport } = useBreakpoints();
  const isMobile = viewport.smallerOrWithin.sm ?? false;

  const { offerId: closingOfferIdURLParam } = useSelector(selectUrlParams);

  const { plan, closingOffers, addons, fiberDiscounts } = cartItems || {};
  const { promo, leaseFeePrice, price } = plan || {};
  const checkForExistingClosingOffers = cartItems?.closingOffers?.find(
    (closingOffer) => closingOffer?.productTypeId === closingOfferIdURLParam,
  );
  const hasClosingOffers = !!checkForExistingClosingOffers?.price;
  const shouldDisplayClosingOffer = !!(closingOfferIdURLParam && hasClosingOffers);

  const fiberDiscount = (fiberDiscounts ?? []).find((fiberDiscount) => fiberDiscount?.name);

  const shouldDisplayThirdMonthFreeService = !!(
    closingOfferIdURLParam &&
    checkForExistingClosingOffers &&
    checkForExistingClosingOffers.price === 0 &&
    checkForExistingClosingOffers.promo?.duration
  );

  const closingOffer =
    closingOffers?.find((closingOffer) => closingOffer?.productTypeId === closingOfferIdURLParam) || {};

  const selectedAddons = ((addons as Array<AddonItem>) || []).reduce<selectedAddons>(
    (acc: selectedAddons, addonItem: AddonItem) => {
      const { price, promo } = addonItem || {};
      const promoDuration = promo?.duration ?? 0;
      return {
        ...acc,
        addonItems: [
          ...acc?.addonItems,
          {
            ...addonItem,
          },
        ],
        duration: acc.duration < promoDuration ? promoDuration : acc.duration,
        total: acc.total + (price || 0) + (promo?.price || 0),
        totalPriceWithoutPromotions: acc.totalPriceWithoutPromotions + -(promo?.price || 0),
      };
    },
    { addonItems: [], total: 0, totalPriceWithoutPromotions: 0, duration: 0 },
  );

  // service section
  const priceWithPromo = Number(price ?? 0) + Number(promo?.price ?? 0);
  const totalPrice = Number(priceWithPromo ?? 0) + Number(leaseFeePrice ?? 0);
  const totalPriceWithPromo = totalPrice + Number(closingOffer?.price ?? 0);
  const totalPriceWithoutPromotions = totalPrice + -Number(promo?.price ?? 0);
  // all cart prices
  const totalCart = totalPrice + Number(selectedAddons.total);
  const totalCartWithPromo = totalPriceWithPromo + Number(selectedAddons.total);
  const totalCartWithoutPromotions =
    totalPriceWithoutPromotions + Number(selectedAddons.total) + Number(selectedAddons.totalPriceWithoutPromotions);

  const calculationData = {
    cartItems,
    selectedAddons,
    shouldDisplayClosingOffer,
    shouldDisplayThirdMonthFreeService,
    fiberDiscount,
    priceClosingOffer: Number(closingOffer?.price ?? 0),
    durationClosingOffer: Number(closingOffer?.promo?.duration ?? 0),
    price: price ?? 0,
    priceWithPromo,
    totalPrice,
    totalPriceWithPromo,
    totalPriceWithoutPromotions,
    totalCart,
    totalCartWithPromo,
    totalCartWithoutPromotions,
    leaseFeePrice: Number(leaseFeePrice),
    isBackgroundAPICallLoading,
  };

  return (
    <Surface className={`${styles['cartContainer']} ${isMobileCartOpened}`} data-testid="cartContainer">
      <CheckoutCartTopper className={`${styles['cardPadding']}`}>
        <div data-testid="topSection">
          {hasFiberPlanInCart ? (
            <FiberPlanCartHeader name={name} characteristics={characteristics} />
          ) : (
            <CheckoutCartNewPlansDescription
              characteristics={characteristics}
              isCafII={isCafII}
              isBusinessUser={isBusinessUser}
              downloadSpeedTranslationKey={translationKeys.header.speedDetails.below}
            />
          )}
        </div>
        {!isMobile && (
          <CheckoutCartDisplayServiceDetails
            displayServiceDetails={displayServiceDetails}
            onClick={onDisplayServiceDetailsClick}
            showLabel={translationKeys.serviceDetails.showLabel}
            hideLabel={translationKeys.serviceDetails.hideLabel}
          />
        )}
        {displayServiceDetails && (
          <>
            <CheckoutCartServiceDetails
              name={name}
              data={calculationData}
              translationKeys={translationKeys.serviceDetails}
            />
            {(selectedAddons?.addonItems?.length && (
              <CheckoutCartOptionalServices
                translations={{
                  edit: translationKeys.addons.edit,
                  free: translationKeys.serviceDetails.free,
                  optionalServices: translationKeys.serviceDetails.optionalServices,
                  priceAfterPromotion: translationKeys.serviceDetails.priceAfterPromotion,
                }}
                addonItems={selectedAddons.addonItems}
                selectedAddonsTotal={selectedAddons?.total}
              />
            )) ||
              ''}
          </>
        )}
        <ContentProvider
          src={createAemUrl(
            aemUrl,
            i18n.language,
            AemFeature.CART_AD,
            productFamilyAemPrefix(isCafII, isChoicePlan, isDiamondPlan, isVs2LargePlan),
            customerType,
          )}
        />
        <CheckoutCartTotalMonthly data={calculationData} translationKeys={translationKeys.totalMonthly} />
        <CheckoutCartDueToday
          dueToday={translationKeys.dueToday.title}
          totalDueToday={translationKeys.dueToday.total}
          calculatedLaterLabel={translationKeys.dueToday.calculatedLater}
          isBackgroundAPICallLoading={isBackgroundAPICallLoading}
          freeDiscountTranslationsKey={translationKeys.dueToday.free}
        />
        <Txt variant="tinyRegular">{translationKeys.extraTaxesDescriptionText}</Txt>
      </CheckoutCartTopper>
    </Surface>
  );
};

export default CheckoutCart;
